/**
 * Métricas reales — wireado contra /api/metrics.
 *
 * Cuatro KPIs principales + bar chart diario (bot vs escalado vs total).
 * El SLA compliance compara el tiempo de primera respuesta contra el target
 * (30 min default). Period picker 7/30/90d.
 */
import { useState } from "react";
import { useParams } from "@tanstack/react-router";
import { BarChart3 } from "lucide-react";
import { findClient } from "@/lib/clients";
import { EmptyState } from "@/components/shared/empty-state";
import { useMetrics, type MetricsPeriod } from "@/hooks/use-metrics";
import { cn } from "@/lib/cn";

const PERIOD_LABEL: Record<MetricsPeriod, string> = {
  "7d": "Últimos 7 días",
  "30d": "Últimos 30 días",
  "90d": "Últimos 90 días",
};

function formatPct(value: number): string {
  return (value * 100).toFixed(1) + "%";
}

function formatMs(ms: number | null): string {
  if (ms === null) return "—";
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function formatNumber(n: number): string {
  return n.toLocaleString("es-AR");
}

export function MetricsPage() {
  const { clientSlug } = useParams({ strict: false }) as { clientSlug?: string };
  const client = findClient(clientSlug);
  const [period, setPeriod] = useState<MetricsPeriod>("7d");

  const { data, isLoading, isError } = useMetrics(client?.slug, period);

  return (
    <div className="min-h-full bg-background overflow-y-auto">
      <div className="max-w-6xl mx-auto px-7 py-8">
        {/* Header */}
        <header className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-sans text-3xl font-semibold tracking-tight text-text-primary">
              Métricas
            </h1>
            <p className="font-sans text-sm text-text-muted mt-1">
              {client
                ? `${client.fullName} · ${PERIOD_LABEL[period]}`
                : `Cross-cliente · ${PERIOD_LABEL[period]}`}
            </p>
          </div>

          <div
            role="radiogroup"
            aria-label="Período"
            className="inline-flex p-[3px] rounded-full bg-surface-2 border border-default"
          >
            {(["7d", "30d", "90d"] as MetricsPeriod[]).map((p) => (
              <button
                key={p}
                role="radio"
                aria-checked={period === p}
                onClick={() => setPeriod(p)}
                className={cn(
                  "h-7 px-3 rounded-full text-xs font-medium font-sans transition-colors",
                  period === p
                    ? "bg-surface-1 text-text-primary shadow-sm"
                    : "text-text-secondary hover:text-text-primary",
                )}
              >
                {p}
              </button>
            ))}
          </div>
        </header>

        {!client ? (
          <EmptyState
            title="Seleccioná un cliente"
            description="Las métricas se ven por cliente desde su workspace."
          />
        ) : isLoading ? (
          <div className="text-sm text-text-muted">Cargando métricas…</div>
        ) : isError || !data ? (
          <EmptyState
            title="No se pudieron cargar las métricas"
            description="Intentá refrescar la página. Si persiste, avisá."
          />
        ) : data.kpi.conversationsTotal === 0 ? (
          <EmptyState
            icon={<BarChart3 size={32} strokeWidth={1.5} />}
            title="Sin conversaciones en el período"
            description="Cuando lleguen DMs o comentarios al cliente, las métricas aparecen acá."
          />
        ) : (
          <MetricsBody
            kpi={data.kpi}
            slaTargetSec={data.slaTargetSec}
            daily={data.dailySeries}
          />
        )}
      </div>
    </div>
  );
}

function MetricsBody({
  kpi,
  slaTargetSec,
  daily,
}: {
  kpi: import("@/hooks/use-metrics").MetricsKpi;
  slaTargetSec: number;
  daily: import("@/hooks/use-metrics").MetricsDayRow[];
}) {
  const slaMin = Math.round(slaTargetSec / 60);
  return (
    <>
      {/* KPI grid */}
      <section
        aria-label="Indicadores clave"
        className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8"
      >
        <KpiCard
          label="Conversaciones"
          value={formatNumber(kpi.conversationsTotal)}
        />
        <KpiCard
          label="Resueltas por bot"
          value={formatPct(kpi.botAutoResolvedPct)}
          secondary={`${formatNumber(kpi.messagesBotResolved)} de ${formatNumber(kpi.conversationsTotal)}`}
          tone="success"
        />
        <KpiCard
          label="Escalación"
          value={formatPct(kpi.escalationRatePct)}
          secondary={`${formatNumber(kpi.messagesEscalated)} escaladas`}
          tone={kpi.escalationRatePct > 0.2 ? "warning" : "default"}
        />
        <KpiCard
          label={`SLA cumplido (${slaMin}m)`}
          value={formatPct(kpi.slaCompliancePct)}
          secondary={`p95 ${formatMs(kpi.p95LatencyMs)}`}
          tone={kpi.slaCompliancePct < 0.9 ? "warning" : "success"}
        />
      </section>

      {/* Daily chart */}
      <section aria-label="Volumen diario" className="card-surface p-5">
        <h3 className="font-sans text-sm font-semibold text-text-primary mb-1">
          Volumen diario
        </h3>
        <p className="font-sans text-xs text-text-muted mb-4">
          Bot resolvió en verde · Escalados a humano en magenta
        </p>
        <DailyChart rows={daily} />
      </section>
    </>
  );
}

function KpiCard({
  label,
  value,
  secondary,
  tone = "default",
}: {
  label: string;
  value: string;
  secondary?: string;
  tone?: "default" | "success" | "warning";
}) {
  return (
    <div className="card-surface p-5">
      <div className="font-sans text-2xs font-medium uppercase tracking-wider text-text-muted mb-2">
        {label}
      </div>
      <div
        className={cn(
          "font-mono text-3xl tabular-nums",
          tone === "success" && "text-success-text",
          tone === "warning" && "text-warning-text",
          tone === "default" && "text-text-primary",
        )}
      >
        {value}
      </div>
      {secondary && (
        <div className="font-sans text-xs text-text-muted mt-1 tabular-nums">
          {secondary}
        </div>
      )}
    </div>
  );
}

function DailyChart({ rows }: { rows: import("@/hooks/use-metrics").MetricsDayRow[] }) {
  if (rows.length === 0) {
    return (
      <div className="py-8 text-center text-sm text-text-muted">
        Sin datos para mostrar
      </div>
    );
  }

  const maxTotal = Math.max(...rows.map((r) => r.total), 1);

  return (
    <div className="flex items-end gap-1 h-48 mt-2" role="img" aria-label="Gráfico diario">
      {rows.map((row) => {
        const botPct = (row.bot / maxTotal) * 100;
        const escPct = (row.escalated / maxTotal) * 100;
        return (
          <div
            key={row.day}
            className="flex-1 flex flex-col items-center gap-1 min-w-0 group"
          >
            <div className="w-full flex flex-col-reverse h-44 relative">
              <div
                className="w-full bg-success-text"
                style={{ height: `${botPct}%` }}
                title={`${row.bot} bot`}
              />
              <div
                className="w-full bg-magenta"
                style={{ height: `${escPct}%` }}
                title={`${row.escalated} escaladas`}
              />
              <span className="absolute inset-x-0 -top-5 font-mono text-2xs tabular-nums text-text-muted opacity-0 group-hover:opacity-100 transition-opacity text-center">
                {row.total}
              </span>
            </div>
            <span className="font-mono text-2xs text-text-muted tabular-nums truncate">
              {row.day.slice(5)}
            </span>
          </div>
        );
      })}
    </div>
  );
}
